// src/pages/Reports/cards/ProfitReportCard.jsx
import React, { useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import * as XLSX from 'xlsx';

import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { fetchProfitReport } from '../../../api';

const TAB_KEYS = [
  { key: 'TODAY', label: 'Today' },
  { key: 'LAST_7', label: '7 Days' },
  { key: 'LAST_30', label: '30 Days' },
  { key: 'THIS_MONTH', label: 'This Month' },
];

const PROFIT_REPORT_COLUMNS = [
  { key: 'Invoice No', label: 'Invoice No', align: 'left', type: 'text', width: 24 },
  { key: 'Date', label: 'Date', align: 'left', type: 'text', width: 14 },
  { key: 'Time', label: 'Time', align: 'left', type: 'text', width: 12 },
  { key: 'Payment Method', label: 'Payment Method', align: 'left', type: 'text', width: 18 },
  { key: 'Item', label: 'Item', align: 'left', type: 'text', width: 32 },
  { key: 'Batch', label: 'Batch', align: 'left', type: 'text', width: 16 },
  { key: 'Qty', label: 'Qty', align: 'right', type: 'number', width: 10 },
  { key: 'Wholesale Price', label: 'Wholesale Price', align: 'right', type: 'money', width: 18 },
  { key: 'Unit Price', label: 'Unit Price', align: 'right', type: 'money', width: 16 },
  { key: 'Bill Discount', label: 'Bill Discount', align: 'right', type: 'money', width: 16 },
  { key: 'Revenue', label: 'Revenue', align: 'right', type: 'money', width: 16 },
  { key: 'Cost', label: 'Cost', align: 'right', type: 'money', width: 16 },
  { key: 'Profit', label: 'Profit', align: 'right', type: 'money', width: 16 },
  { key: 'Margin', label: 'Margin', align: 'right', type: 'percent', width: 14 },
  { key: 'Invoice Total', label: 'Invoice Total', align: 'right', type: 'money', width: 16 },
  { key: 'Invoice Profit', label: 'Invoice Profit', align: 'right', type: 'money', width: 16 },
];

const getDateString = (date) => date.toISOString().slice(0, 10);

const getDateRangeFromTab = (tabKey) => {
  const today = new Date();
  const to = getDateString(today);

  if (tabKey === 'TODAY') {
    return { from: to, to };
  }

  if (tabKey === 'LAST_7') {
    const fromDate = new Date();
    fromDate.setDate(today.getDate() - 6);
    return { from: getDateString(fromDate), to };
  }

  if (tabKey === 'LAST_30') {
    const fromDate = new Date();
    fromDate.setDate(today.getDate() - 29);
    return { from: getDateString(fromDate), to };
  }

  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  return { from: getDateString(monthStart), to };
};

const formatMoney = (value) => {
  return `Rs. ${Number(value || 0).toLocaleString('en-LK', {
    maximumFractionDigits: 2,
  })}`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString('en-LK');

const formatPercent = (value) => {
  return `${Number(value || 0).toLocaleString('en-LK', {
    maximumFractionDigits: 2,
  })}%`;
};

const formatExcelDateOnly = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-LK');
};

const formatExcelTimeOnly = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleTimeString('en-LK', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const setColumnWidths = (worksheet, widths) => {
  worksheet['!cols'] = widths.map((width) => ({ wch: width }));
};

const formatCellValue = (value, type) => {
  if (type === 'money') return formatMoney(value);
  if (type === 'number') return formatNumber(value);
  if (type === 'percent') return formatPercent(value);
  return value || '';
};

const buildProfitReportRows = (reportData) => {
  return (reportData?.itemProfitDetails || []).map((item) => ({
    'Invoice No': item.invoiceNo || '',
    Date: formatExcelDateOnly(item.dateTime),
    Time: formatExcelTimeOnly(item.dateTime),
    'Payment Method': item.paymentMethod || '',
    Item: item.itemName || '',
    Batch: item.batchNo || '',
    Qty: item.qty || 0,
    'Wholesale Price': item.wholesalePrice || 0,
    'Unit Price': item.unitPriceAfterDiscount || item.unitPrice || 0,
    'Bill Discount': item.billDiscountShare || 0,
    Revenue: item.finalLineRevenue || 0,
    Cost: item.lineCost || 0,
    Profit: item.finalLineProfit || 0,
    Margin: item.profitMargin || 0,
    'Invoice Total': item.invoiceTotal || 0,
    'Invoice Profit': item.invoiceGrossProfit || 0,
  }));
};

const downloadProfitExcel = ({ filename, reportData }) => {
  if (!reportData) return;

  const profitRows = buildProfitReportRows(reportData);

  const sheetRows = [
    PROFIT_REPORT_COLUMNS.map((column) => column.label),
    ...profitRows.map((row) =>
      PROFIT_REPORT_COLUMNS.map((column) => row[column.key])
    ),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);

  setColumnWidths(
    worksheet,
    PROFIT_REPORT_COLUMNS.map((column) => column.width)
  );

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Profit Report');

  XLSX.writeFile(workbook, filename);
};

const ProfitReportCard = () => {
  const defaultRange = getDateRangeFromTab('THIS_MONTH');

  const [tab, setTab] = React.useState('THIS_MONTH');
  const [from, setFrom] = React.useState(defaultRange.from);
  const [to, setTo] = React.useState(defaultRange.to);

  const [reportData, setReportData] = React.useState(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const loadReport = async (params = { from, to }) => {
    try {
      setLoading(true);
      setError('');

      const res = await fetchProfitReport(params);
      setReportData(res.data);
    } catch (err) {
      console.error('Failed to fetch profit report:', err);
      setError(err?.response?.data?.message || 'Failed to load profit report.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport({ from, to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (_e, value) => {
    setTab(value);

    const range = getDateRangeFromTab(value);
    setFrom(range.from);
    setTo(range.to);

    loadReport(range);
  };

  const handleFromChange = (e) => {
    const value = e.target.value;
    setFrom(value);
    setTab(false);
    loadReport({ from: value, to });
  };

  const handleToChange = (e) => {
    const value = e.target.value;
    setTo(value);
    setTab(false);
    loadReport({ from, to: value });
  };

  const summary = reportData?.summary || {};
  const paymentBreakdown = summary.paymentBreakdown || {};
  const previewRows = buildProfitReportRows(reportData);

  const handleDownload = () => {
    downloadProfitExcel({
      filename: `profit-report-${from}-to-${to}.xlsx`,
      reportData,
    });
  };

  return (
    <>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '22px',
          p: { xs: 2, sm: 3 },
          border: '1px solid #e5e7eb',
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.07)',
          background: '#fff',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: '16px',
              background: '#ecfdf5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <TrendingUpOutlinedIcon
              sx={{
                color: '#059669',
                fontSize: 30,
              }}
            />
          </Box>

          <Chip
            label={`${formatNumber(summary.invoiceCount)} ${
                summary.invoiceCount === 1 ? 'invoice' : 'invoices'
            }`}
            sx={{
              borderRadius: '999px',
              background: '#d1fae5',
              color: '#065f46',
              fontWeight: 800,
              fontSize: 14,
              height: 34,
              px: 1,
            }}
          />
        </Box>

        <Typography
          sx={{
            fontSize: 23,
            fontWeight: 900,
            color: '#111827',
            mb: 1,
          }}
        >
          Profit Report
        </Typography>

        <Typography
          sx={{
            fontSize: 15.5,
            lineHeight: 1.65,
            color: '#6b7280',
            mb: 2.5,
          }}
        >
          Revenue, cost, gross profit, margins and item-level profit details for a selected date range.
        </Typography>

        <Box
          sx={{
            mb: 2.5,
            borderRadius: '20px',
            backgroundColor: '#f3f4f6',
            p: 1.4,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(4, 1fr)',
              },
              gap: 0.8,
            }}
          >
            {TAB_KEYS.map((item) => {
              const active = tab === item.key;

              return (
                <Button
                  key={item.key}
                  onClick={() => handleTabChange(null, item.key)}
                  disableRipple
                  sx={{
                    height: 40,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 900,
                    fontSize: { xs: '0.82rem', sm: '0.9rem' },
                    letterSpacing: '0.01em',
                    backgroundColor: active ? '#ffffff' : 'transparent',
                    color: active ? '#111827' : '#6b7280',
                    boxShadow: active
                      ? 'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), 0 4px 12px rgba(15, 23, 42, 0.10)'
                      : 'none',
                    '&:hover': {
                      backgroundColor: active ? '#ffffff' : 'rgba(255,255,255,0.45)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>

          <Box
            sx={{
              height: '1px',
              backgroundColor: '#e5e7eb',
              mt: 1.4,
              mb: 1.4,
            }}
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
              },
              gap: 1.4,
            }}
          >
            <DateInput label="From" value={from} onChange={handleFromChange} />
            <DateInput label="To" value={to} onChange={handleToChange} />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(3, 1fr)',
            },
            gap: 1.2,
            mb: 2.5,
          }}
        >
          <MiniMetric label="Gross Profit" value={formatMoney(summary.grossProfit)} />
          <MiniMetric label="Total Cost" value={formatMoney(summary.totalCost)} />
          <MiniMetric label="Profit Margin" value={formatPercent(summary.profitMargin)} />
        </Box>

        <Box sx={{ mt: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '150px 1fr',
            },
            gap: 1.4,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setPreviewOpen(true)}
            disabled={loading || !reportData}
            sx={{
              height: 50,
              borderRadius: '12px',
              borderColor: '#e5e7eb',
              color: '#374151',
              fontWeight: 800,
              fontSize: 15,
              textTransform: 'none',
              background: '#fff',
              '&:hover': {
                borderColor: '#cbd5e1',
                background: '#f9fafb',
              },
            }}
          >
            Preview
          </Button>

          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <FileDownloadOutlinedIcon />
              )
            }
            onClick={handleDownload}
            disabled={loading || !reportData}
            sx={{
              height: 50,
              borderRadius: '12px',
              background: '#0f172a',
              color: '#fff',
              fontWeight: 900,
              fontSize: 15,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                background: '#111827',
                boxShadow: 'none',
              },
              '&:disabled': {
                background: '#94a3b8',
                color: '#fff',
              },
            }}
          >
            Download Excel
          </Button>
        </Box>
        </Box>
      </Card>

      <ProfitReportPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        from={from}
        to={to}
        summary={summary}
        paymentBreakdown={paymentBreakdown}
        rows={previewRows}
      />
    </>
  );
};

const DateInput = ({ label, value, onChange }) => {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: '#6b7280',
          mb: 0.7,
          ml: 0.3,
        }}
      >
        {label}
      </Typography>

      <TextField
        type="date"
        size="small"
        value={value}
        onChange={onChange}
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            height: 52,
            borderRadius: '15px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
            '& fieldset': {
              border: 'none',
            },
            '&:hover': {
              borderColor: '#d1d5db',
            },
            '&.Mui-focused': {
              borderColor: '#94a3b8',
              boxShadow: '0 0 0 3px rgba(148, 163, 184, 0.18)',
            },
          },
          '& .MuiOutlinedInput-input': {
            fontSize: '1rem',
            fontWeight: 800,
            color: '#333',
            px: 1.6,
          },
        }}
      />
    </Box>
  );
};

const MiniMetric = ({ label, value }) => {
  return (
    <Box
      sx={{
        borderRadius: '14px',
        background: '#f8fafc',
        border: '1px solid #eef2f7',
        p: 1.4,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: '#6b7280',
          fontWeight: 800,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: 15,
          color: '#111827',
          fontWeight: 900,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const ProfitReportPreviewModal = ({
  open,
  onClose,
  from,
  to,
  summary,
  paymentBreakdown,
  rows,
}) => {
  const visibleRows = rows || [];

  const cash = paymentBreakdown.cash || {};
  const card = paymentBreakdown.card || {};
  const other = paymentBreakdown.other || {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '22px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 900,
              color: '#111827',
            }}
          >
            Profit Report Preview
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: '#6b7280',
              mt: 0.4,
            }}
          >
            {from} to {to}
          </Typography>
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          backgroundColor: '#f8fafc',
        }}
      >
        <Box sx={{ p: 2.5, backgroundColor: '#ffffff' }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 900,
              color: '#111827',
              mb: 1.5,
            }}
          >
            Summary
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 1.4,
            }}
          >
            <PreviewMetric label="Total Sales" value={formatMoney(summary.totalSales)} />
            <PreviewMetric label="Total Cost" value={formatMoney(summary.totalCost)} />
            <PreviewMetric label="Gross Profit" value={formatMoney(summary.grossProfit)} />
            <PreviewMetric label="Profit Margin" value={formatPercent(summary.profitMargin)} />
            <PreviewMetric label="Invoices" value={formatNumber(summary.invoiceCount)} />
            <PreviewMetric label="Items Sold" value={formatNumber(summary.itemsSold)} />
            <PreviewMetric label="Average Profit / Invoice" value={formatMoney(summary.averageProfitPerInvoice)} />
            <PreviewMetric label="Total Discounts" value={formatMoney(summary.totalDiscount)} />
            <PreviewMetric label="Cash Profit" value={formatMoney(cash.profit)} />
            <PreviewMetric label="Card Profit" value={formatMoney(card.profit)} />
            <PreviewMetric label="Other Profit" value={formatMoney(other.profit)} />
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#ffffff',
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 900,
              color: '#111827',
            }}
          >
            Profit Details
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: '#6b7280',
              fontWeight: 600,
              mt: 0.4,
            }}
          >
            These are the same columns included in the downloaded Excel sheet.
          </Typography>
        </Box>

        {visibleRows.length === 0 ? (
          <Box sx={{ p: 4 }}>
            <Typography color="text.secondary">No profit data found for this date range.</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: '60vh',
              overflow: 'auto',
              backgroundColor: '#ffffff',
            }}
          >
            <Box
              sx={{
                p: 2,
                backgroundColor: '#ffffff',
              }}
            >
              <Box
                sx={{
                  maxHeight: '55vh',
                  overflow: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: '14px',
                  backgroundColor: '#ffffff',
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 1700,
                    '& th': {
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      fontWeight: 900,
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid #d1d5db',
                    },
                    '& td': {
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid #eef2f7',
                      fontSize: 13,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {PROFIT_REPORT_COLUMNS.map((column) => (
                        <TableCell key={column.key} align={column.align}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {visibleRows.map((row, index) => (
                      <TableRow key={`${row['Invoice No']}-${index}`}>
                        {PROFIT_REPORT_COLUMNS.map((column) => (
                          <TableCell key={column.key} align={column.align}>
                            {formatCellValue(row[column.key], column.type)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PreviewMetric = ({ label, value }) => {
  return (
    <Box
      sx={{
        backgroundColor: '#f8fafc',
        border: '1px solid #e5e7eb',
        borderRadius: '14px',
        p: 1.5,
      }}
    >
      <Typography
        sx={{
          color: '#6b7280',
          fontSize: 12,
          fontWeight: 800,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: '#111827',
          fontSize: 17,
          fontWeight: 900,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export default ProfitReportCard;